import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { ListResponse } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { ConfirmationModal, ModalControlModalProps, Text, TextLink } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { getPhoneTreeOverrideUsage } from '../helpers/graph/node-utils';

interface PhoneTreeOverrideUsageModalProps {
  modalProps: ModalControlModalProps;
  processingNodeId: string;
  overrides: ListResponse;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}

export const PhoneTreeOverrideUsageModal = ({
  modalProps,
  processingNodeId,
  overrides,
  nodes,
  edges,
}: PhoneTreeOverrideUsageModalProps) => {
  const { t } = useTranslation('phone');
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const phoneTreeNodes = getPhoneTreeOverrideUsage({
    nodeId: processingNodeId,
    nodes,
    edges,
    overrides,
  });

  const phoneTreeNode = Object.values(phoneTreeNodes)[0];

  if (!phoneTreeNode) {
    return null;
  }

  const overrideUsageCount = phoneTreeNode.overrides.length;

  const phoneTreeNodeName = phoneTreeNode.callObject?.primitiveName ?? t('A Phone Tree Node');

  return (
    <ConfirmationModal
      {...modalProps}
      maxWidth={600}
      title={t('Cannot Delete Phone Tree')}
      message={
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          <Text>
            {t(
              overrideUsageCount === 1
                ? `${phoneTreeNodeName} can't be deleted because it is currently in use in an Override. The Phone Tree must be removed from this override in order to be deleted.`
                : `${phoneTreeNodeName} can't be deleted because it is currently in use in Overrides. The Phone Tree must be removed from these overrides in order to be deleted.`
            )}
          </Text>

          <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {phoneTreeNode?.overrides.map((override) => (
              <TextLink
                key={override.callRouteId}
                onClick={() =>
                  settingsNavigate({
                    to: '/phone/overrides',
                    context: { callRouteId: override.callRouteId, openEditModal: true },
                  })
                }
              >
                {override.callRouteName}
              </TextLink>
            ))}
          </div>
        </div>
      }
      confirmLabel=''
      cancelLabel={t('Close')}
      onConfirm={() => {}}
      onCancel={modalProps.onClose}
      cancelTrackingId={trackingId({
        context: 'setting',
        feature: 'call-routes',
        details: 'delete::phone-tree-cannot-delete-cancel-btn',
      })}
      show
      onClose={modalProps.onClose}
    />
  );
};
